import { useState, useRef } from "react";
import { Edit, Trash2, Plus, Receipt, Upload, Keyboard, Loader2, FileText, AlertCircle, Eye } from "lucide-react";
import { toast } from "sonner";
import apiClient from "../../services/api";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { NumericInput } from "../ui/numeric-input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { formatCurrency } from "./utils";
import { formatDate } from "../../lib/formatters";
import { sortAlphabetically } from "../../lib/sortUtils";
const getTodayDate = () => new Date().toISOString().split("T")[0];

const InvoiceCard = ({
  title = "Invoices",
  vendorLabel = "Supplier",
  invoices = [],
  suppliers = [],
  canManage = false,
  onAdd,
  onEdit,
  onDelete,
  onTogglePaid,
  testIdPrefix = "invoice",
  jobId,
  getAuthHeaders,
  onNewSupplierNeeded,
}) => {
  const [choiceDialogOpen, setChoiceDialogOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [newSupplierPrompt, setNewSupplierPrompt] = useState(null);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    supplier_id: "",
    invoice_number: "",
    amount: "",
    date: getTodayDate(),
    paid: false,
    file_id: null,
    description: "",
  });

  const sortedSuppliers = sortAlphabetically(suppliers);

  const outstandingAmount = invoices
    .filter(inv => !inv.paid)
    .reduce((sum, inv) => sum + (inv.amount || 0), 0);

  const resetForm = () => {
    setForm({
      supplier_id: "",
      invoice_number: "",
      amount: "",
      date: getTodayDate(),
      paid: false,
      file_id: null,
      description: "",
    });
    setNewSupplierPrompt(null);
  };

  const handleChoiceEnter = () => {
    setChoiceDialogOpen(false);
    resetForm();
    setDialogOpen(true);
  };

  const handleChoiceUpload = () => {
    setChoiceDialogOpen(false);
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    if (!jobId || !getAuthHeaders) {
      toast.error("Upload not available");
      return;
    }

    setScanning(true);
    setDialogOpen(true);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await apiClient.post(
        `/jobs/${jobId}/invoices/scan`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const { file_id, ai_data } = res.data;

      // Pre-fill form with AI results
      setForm({
        supplier_id: ai_data.matched_supplier_id || "",
        invoice_number: ai_data.invoice_number || "",
        amount: ai_data.amount ? String(ai_data.amount) : "",
        date: getTodayDate(),
        paid: false,
        file_id: file_id,
        description: ai_data.description || "",
      });

      // If no supplier match, show prompt
      if (!ai_data.matched_supplier_id && ai_data.supplier_name) {
        setNewSupplierPrompt(ai_data.supplier_name);
      }

      toast.success("Invoice scanned — please review the extracted data");
    } catch {
      toast.error("Failed to scan invoice");
      resetForm();
    } finally {
      setScanning(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const success = await onAdd({
      supplier_id: form.supplier_id,
      invoice_number: form.invoice_number,
      amount: parseFloat(form.amount),
      date: form.date,
      paid: form.paid,
      file_id: form.file_id,
      description: form.description,
    });
    if (success) {
      setDialogOpen(false);
      resetForm();
    }
  };

  const openEdit = (inv) => {
    setEditingInvoice(inv);
    setForm({
      supplier_id: inv.supplier_id,
      invoice_number: inv.invoice_number,
      amount: inv.amount.toString(),
      date: inv.date,
      paid: inv.paid,
      file_id: inv.file_id || null,
      description: inv.description || "",
    });
    setEditDialogOpen(true);
  };

  const handleEdit = async () => {
    const success = await onEdit(editingInvoice.id, {
      supplier_id: form.supplier_id,
      invoice_number: form.invoice_number,
      amount: parseFloat(form.amount),
      date: form.date,
      paid: form.paid,
      file_id: form.file_id,
      description: form.description,
    });
    if (success) {
      setEditDialogOpen(false);
      setEditingInvoice(null);
      resetForm();
    }
  };

  const handleAddNewSupplier = () => {
    if (onNewSupplierNeeded && newSupplierPrompt) {
      onNewSupplierNeeded(newSupplierPrompt);
    }
  };

  const viewInvoiceFile = async (fileId) => {
    if (!fileId) return;
    try {
      // Invoice file endpoint requires auth — fetch with token then open blob URL
      const response = await apiClient.get(`/jobs/invoice-files/${fileId}/file`, {
        responseType: "blob",
      });
      const blobUrl = window.URL.createObjectURL(response.data);
      const newTab = window.open(blobUrl, "_blank");
      // Revoke the object URL once the tab has loaded to free memory
      if (newTab) {
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
      }
    } catch (err) {
      toast.error(err.response?.status === 404 ? "Invoice file not found" : "Failed to load invoice file");
    }
  };

  const renderFormFields = (isEditMode) => (
    <div className="space-y-4">
      {scanning && (
        <div className="flex items-center justify-center gap-2 p-6 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Scanning invoice with AI...</span>
        </div>
      )}

      {!scanning && (
        <>
          {newSupplierPrompt && !form.supplier_id && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-2">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-amber-800">Supplier not found: "{newSupplierPrompt}"</p>
                  <p className="text-amber-700">Select an existing supplier below, or add this as a new supplier in Settings.</p>
                </div>
              </div>
              {onNewSupplierNeeded && (
                <Button variant="outline" size="sm" onClick={handleAddNewSupplier} data-testid="add-new-supplier-btn">
                  Add "{newSupplierPrompt}" as new supplier
                </Button>
              )}
            </div>
          )}

          {form.file_id && (
            <div className="flex items-center gap-2 p-2 bg-muted/50 rounded text-sm">
              <FileText className="w-4 h-4 text-muted-foreground" />
              <span>Scanned invoice attached</span>
              <Button variant="ghost" size="sm" className="ml-auto h-7" onClick={() => viewInvoiceFile(form.file_id)}>
                <Eye className="w-3 h-3 mr-1" /> View
              </Button>
            </div>
          )}

          <div className="form-group">
            <Label>{vendorLabel} *</Label>
            <Select value={form.supplier_id} onValueChange={(v) => setForm(prev => ({ ...prev, supplier_id: v }))}>
              <SelectTrigger data-testid={`${testIdPrefix}-supplier-select`}>
                <SelectValue placeholder={`Select ${vendorLabel.toLowerCase()}`} />
              </SelectTrigger>
              <SelectContent>
                {sortedSuppliers.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {suppliers.length === 0 && (
              <p className="text-xs text-muted-foreground mt-1">Add vendors in Settings → Suppliers</p>
            )}
          </div>
          <div className="form-group">
            <Label>Invoice # *</Label>
            <Input
              value={form.invoice_number}
              onChange={(e) => setForm(prev => ({ ...prev, invoice_number: e.target.value }))}
              required
              data-testid={`${testIdPrefix}-number-input`}
            />
          </div>
          <div className="form-group">
            <Label>Amount *</Label>
            <NumericInput
              decimals={2}
              allowNegative
              value={form.amount}
              onChange={(e) => setForm(prev => ({ ...prev, amount: e.target.value }))}
              data-testid={`${testIdPrefix}-amount-input`}
            />
          </div>
          <div className="form-group">
            <Label>Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Line items or description..."
              rows={2}
              data-testid={`${testIdPrefix}-description-input`}
            />
          </div>
          <div className="form-group">
            <Label>Date *</Label>
            <Input
              type="date"
              value={form.date}
              onChange={(e) => setForm(prev => ({ ...prev, date: e.target.value }))}
              required
              data-testid={`${testIdPrefix}-date-input`}
            />
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id={`${testIdPrefix}-paid-${isEditMode ? 'edit' : 'add'}`}
              checked={form.paid}
              onCheckedChange={(c) => setForm(prev => ({ ...prev, paid: c }))}
            />
            <Label htmlFor={`${testIdPrefix}-paid-${isEditMode ? 'edit' : 'add'}`}>Paid</Label>
          </div>
          <Button
            type={isEditMode ? "button" : "submit"}
            onClick={isEditMode ? handleEdit : undefined}
            className="w-full"
            disabled={!form.supplier_id}
            data-testid={`${testIdPrefix}-submit-btn`}
          >
            {isEditMode ? "Save Changes" : "Add Invoice"}
          </Button>
        </>
      )}
    </div>
  );

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg flex items-center gap-2">
          <Receipt className="w-5 h-5" />
          {title}
        </CardTitle>
        {canManage && (
          <Button size="sm" onClick={() => setChoiceDialogOpen(true)} data-testid={`add-${testIdPrefix}-btn`}>
            <Plus className="w-4 h-4 mr-2" />
            Add Invoice
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {invoices.length > 0 ? (
          <>
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>{vendorLabel}</TableHead>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-center">Paid</TableHead>
                  <TableHead className="w-20"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((inv) => {
                  const isReceipt = inv.source === "receipt" || (inv.invoice_number && inv.invoice_number.startsWith("RCP-"));
                  const hasFile = isReceipt ? !!inv.receipt_id : !!inv.file_id;
                  return (
                    <TableRow key={inv.id} className="table-row">
                      <TableCell>
                        {inv.supplier_name}
                        {isReceipt && (
                          <span className="ml-2 text-xs text-muted-foreground">(Receipt)</span>
                        )}
                      </TableCell>
                      <TableCell>{inv.invoice_number}</TableCell>
                      <TableCell>{formatDate(inv.date)}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(inv.amount, true)}</TableCell>
                      <TableCell className="text-center">
                        <Checkbox
                          checked={inv.paid}
                          onCheckedChange={() => onTogglePaid(inv)}
                          disabled={!canManage}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {/* View file button (for receipts or uploaded invoices) */}
                          {isReceipt && inv.receipt_id && (
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => window.open(`/api/receipts/${inv.receipt_id}/file`, "_blank")} title="View receipt" data-testid={`view-receipt-${inv.id}`}>
                              <Eye className="w-4 h-4" />
                            </Button>
                          )}
                          {!isReceipt && inv.file_id && (
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => viewInvoiceFile(inv.file_id)} title="View invoice file" data-testid={`view-invoice-${inv.id}`}>
                              <Eye className="w-4 h-4" />
                            </Button>
                          )}
                          {/* Edit/Delete only for managers, and not for receipts */}
                          {canManage && !isReceipt && (
                            <>
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(inv)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive">
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Invoice?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This will permanently remove this invoice.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => onDelete(inv.id)}
                                      className="bg-destructive text-destructive-foreground"
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <div className="mt-4 p-4 bg-red-50 rounded-lg border border-red-200">
              <div className="flex justify-between items-center">
                <span className="font-medium text-red-800">Outstanding</span>
                <span className="text-xl font-bold text-red-700">{formatCurrency(outstandingAmount, true)}</span>
              </div>
            </div>
          </>
        ) : (
          <p className="text-muted-foreground text-center py-4">No invoices recorded</p>
        )}
      </CardContent>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.gif,.webp,.heic,.heif,.pdf"
        onChange={handleFileUpload}
        className="hidden"
        data-testid={`${testIdPrefix}-file-input`}
      />

      {/* Choice Dialog: Enter or Upload */}
      <Dialog open={choiceDialogOpen} onOpenChange={setChoiceDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Invoice</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <Button
              variant="outline"
              className="h-24 flex flex-col items-center gap-2"
              onClick={handleChoiceEnter}
              data-testid={`${testIdPrefix}-enter-manually-btn`}
            >
              <Keyboard className="w-6 h-6" />
              <span>Enter Manually</span>
            </Button>
            <Button
              variant="outline"
              className="h-24 flex flex-col items-center gap-2"
              onClick={handleChoiceUpload}
              data-testid={`${testIdPrefix}-upload-btn`}
            >
              <Upload className="w-6 h-6" />
              <span>Upload & Scan</span>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Invoice Dialog (manual entry or AI-prefilled) */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{form.file_id ? "Review Scanned Invoice" : `Add ${title.replace(/s$/, "")}`}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAdd}>
            {renderFormFields(false)}
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Invoice Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Invoice</DialogTitle>
          </DialogHeader>
          {renderFormFields(true)}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default InvoiceCard;
